import { useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Callout from 'modules/core/components/Callout'
import Modal from 'modules/core/components/Modal'
import SelectField from 'modules/core/components/SelectField'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import countryList from 'modules/core/lib/countryList'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { getCurrencyCode } from 'modules/core/lib/money'
import app from 'modules/core/store/models/app'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { useCreatePayrollPolicyWithEmployees } from 'modules/payroll_bundle/hooks/useCreatePayrollPolicy'
import CurrencySelector from 'modules/payroll_bundle/pages/SelfServicePage/components/payrollPolicyStep/CurrencySelector'
import EmployeesSelector from 'modules/payroll_bundle/pages/SelfServicePage/components/payrollPolicyStep/EmployeesSelector'
import PeriodicitySelector from 'modules/payroll_bundle/pages/SelfServicePage/components/payrollPolicyStep/PeriodicitySelector'
import type { RecurrenceType } from 'modules/payroll_bundle/pages/SelfServicePage/types'
import { PeriodicityType } from 'modules/payroll_bundle/store/models/Policy'
import Policy, { MONTHLY } from 'modules/payroll_bundle/store/models/Policy'

interface PayrollPolicySelfServiceFormValues {
  name: string
  periodicity: PeriodicityType | ''
  starts_on: string
  currency: string
  country: string
  recurrence: RecurrenceType | ''
  employees_selected: number[]
}

interface Props {
  onClose: () => void
  contractVersions: ContractVersions
}

const PayrollPolicySelfServiceForm = ({ onClose, contractVersions }: Props) => {
  const [submittingPolicy, setSubmittingPolicy] = useState(false)

  const periodicities = Policy.periodicities()
  const { company } = useCurrentSession()

  const { navigation } = useNavigation()

  const defaultRecurrence = periodicities[MONTHLY].recurrence as RecurrenceType
  const { CreatePayrollPolicyWithEmployees } = useCreatePayrollPolicyWithEmployees()

  const form = useForm<PayrollPolicySelfServiceFormValues>({
    defaultValues: {
      name: '',
      periodicity: MONTHLY,
      starts_on: '',
      currency: '',
      country: '',
      recurrence: defaultRecurrence,
      employees_selected: [],
    },
    onSubmit: async (data) => {
      setSubmittingPolicy(true)
      const employees = data.employees_selected.map(String)
      const result = await CreatePayrollPolicyWithEmployees({
        name: data.name,
        recurrence: data.recurrence,
        startsOn: data.starts_on,
        currency: data.currency,
        country: data.country,
        companyId: company.id,
        employeeIds: employees,
      })
      const handler = handleResult(result, () => null)
      setSubmittingPolicy(false)

      if (handler.success) {
        const payrollPolicyId = handler.data?.policy?.id

        if (payrollPolicyId) {
          track(trackingEvents.payroll.userCreatesNewPolicy, {
            company_id: company.id,
            periodicity: data.periodicity,
            currency: data.currency,
            country: data.country,
          })

          app.setFlash({
            type: 'correct',
            message: i18n.t('payroll_bundle.onboarding.correct'),
          })

          onClose()

          window.location.href = navigation.settings.payroll_bundle.fullPath()
        }
      } else if (!handler.success) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('payroll_bundle.onboarding.error'),
        })
      }

      return handler
    },
  })

  const canSubmitForm =
    !submittingPolicy &&
    form.values.starts_on !== '' &&
    form.values.recurrence !== '' &&
    form.values.name !== '' &&
    form.values.currency !== '' &&
    form.values.country !== '' &&
    form.values.employees_selected.length > 0

  const countriesList = countryList[i18n.locale as keyof typeof countryList] || countryList.en

  return (
    <Modal
      illustration="money-bag"
      title={i18n.t('app_store_v2.apps.payroll_bundle.settings.payroll_policy.title')}
      description={i18n.t('app_store_v2.apps.payroll_bundle.settings.payroll_policy.description')}
      onClose={onClose}
      footer={
        <Button
          onClick={() => form.handleSubmit()}
          disabled={!canSubmitForm}
          loading={submittingPolicy}
        >
          {i18n.t('app_store_v2.apps.payroll_bundle.settings.payroll_policy.add_policy')}
        </Button>
      }
    >
      <Box gap="s16" paddingTop="s32" paddingBottom="s32">
        <Box>
          <TextField
            label={i18n.t('payroll_bundle.onboarding.payroll_policy_step.name_input')}
            value={form.values.name}
            onChange={(value) => {
              form.setValues({ ...form.values, name: value })
            }}
          />
        </Box>

        <SelectField
          label={i18n.t('address_input.country')}
          onChange={(value) => {
            form.setValues({ ...form.values, country: value, currency: getCurrencyCode(value)! })
          }}
          value={form.values.country ?? ''}
          options={countriesList}
          none={i18n.t('common.choose')}
        />

        <CurrencySelector
          currencyValue={form.values.currency}
          onChange={(value) => form.setValues({ ...form.values, currency: value })}
        />

        <PeriodicitySelector
          periodicityValue={form.values.periodicity}
          startOnValue={form.values.starts_on}
          onChangeStartDate={(value) => {
            form.setValues({ ...form.values, starts_on: value })
          }}
          onChangePeriodicity={(value) => {
            form.setValues({
              ...form.values,
              recurrence: value.recurrence,
              periodicity: value.periodicity,
              starts_on: value.starts_on,
            })
          }}
        />

        <Box gap="s16">
          <Text.Normal size="200" weight="medium" color="grey1000">
            {i18n.t('payroll_bundle.onboarding.payroll_policy_step.employees_selector')}
          </Text.Normal>
          <EmployeesSelector
            onChange={(value) => {
              form.setValues({ ...form.values, employees_selected: value })
            }}
            country={form.values.country}
            currency={form.values.currency}
            selectedEmployeeIds={form.values.employees_selected || []}
            filterProps={{
              hideFilterByStatus: false,
              showFilterByTeam: true,
              showFilterByLegalEntity: true,
            }}
            contractVersions={contractVersions}
          />

          <Callout
            title={i18n.t('payroll_bundle.onboarding.payroll_policy_step.employees_selector_title')}
            type="info"
            description={i18n.t(
              'payroll_bundle.onboarding.payroll_policy_step.form_tips.employees'
            )}
          />
        </Box>
      </Box>
    </Modal>
  )
}

export default observer(PayrollPolicySelfServiceForm)
